/** Shared HTML for the inbox preview and the Cloudflare preview send. */

import { directionOf } from "./text-direction";
import { appendUtm, type UtmParams } from "./utm";

export interface PreviewArticle {
  title: string;
  url: string;
  category?: string;
}

export interface PreviewBlock {
  id?: string;
  type: "heading" | "text" | "image" | "link" | "article" | "divider" | "hero" | "tip" | "cta" | "footer";
  text?: string;
  url?: string;
  alt?: string;
}

export interface PreviewIssue {
  title: string;
  subject: string;
  notes?: string;
  preheader?: string;
  sender_name?: string;
  reply_to?: string;
  first_name?: string;
  utm?: UtmParams;
  articles: PreviewArticle[];
  blocks?: PreviewBlock[];
  /** The category heading above the content, e.g. "التغذية". */
  category_heading?: string;
  /** Supersedes `blocks` when present. */
  sections?: PreviewSection[];
}

/**
 * A newsletter section as the renderer sees it: articles already resolved.
 *
 * Resolution happens upstream — the editor pins articles, or the send
 * pipeline picks them from the recipient's category. By the time HTML is
 * produced there is nothing dynamic left, which keeps this renderer pure and
 * usable from both the app and the Worker.
 */
export interface PreviewSectionArticle {
  /** Where the picture sits around the link and its description. */
  layout?: "stacked" | "image-below" | "image-start" | "image-end" | "text";
  title: string;
  url: string;
  excerpt?: string;
  image?: string;
  category?: string;
  read_minutes?: number;
}

export interface PreviewSection {
  id: string;
  type:
    | "category" | "featured" | "articles" | "banner" | "tip" | "doctor"
    | "block" | "text" | "image" | "cta" | "divider" | "spacer" | "footer";
  heading?: string;
  layout?: "list" | "grid" | "stacked" | "image-below" | "image-start" | "image-end" | "text" | "overlay";
  articles?: PreviewSectionArticle[];
  text?: string;
  url?: string;
  image?: string;
  cta_label?: string;
}

export interface BrandEmailOptions {
  title: string;
  /** The line beside the logo: what this is and when it went out. */
  masthead?: string;
  preheader?: string;
  heading?: string;
  iconUrl?: string;
  bodyHtml: string;
  dir?: "ltr" | "rtl";
  footerLines?: string[];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Altibbi's published design tokens, from brand.altibbi.app/api/tokens.json
 * (version e2fec321). The brand hub says it plainly: pull it, don't retype it —
 * so every value here is the token's exact published value, and the token name
 * is in the comment so a drift is obvious on sight.
 *
 * These were briefly replaced with a neutral grey palette borrowed from
 * another product. Restraint was the right lesson to take from it; its colours
 * were not ours to take.
 */
const B = {
  bg: "#F7F7F8",      // surface-2 — app ground, insets
  card: "#FFFFFF",    // surface   — cards, panels
  // Depth comes from the hairline and a tone shift, never from a shadow: a
  // drop shadow is the first thing that makes an email look dated, and half
  // the clients drop it anyway.
  border: "#E6E6E8",  // line      — hairline borders, dividers
  hair: "#F0F1F3",    // surface-3 — the lighter rule between list rows
  t1: "#17181A",      // ink       — headings, primary text
  t2: "#50545B",      // ink-2     — body copy
  t3: "#6B7078",      // ink-3     — captions, muted
  blue: "#155EEF",      // brand        — primary CTA, body links, brand fills
  blueDeep: "#0C4ED4",  // brand-hover  — button hover / pressed
  blueMid: "#427CEF",   // brand-400    — mid tint
  blueSoft: "#C1D3F6",  // brand-border — brand-tinted borders
  tint: "#E9EFFB",      // brand-soft   — tag fills, soft emphasis
  font: "'IBM Plex Sans Arabic',-apple-system,'Segoe UI',Arial,sans-serif",
  display: "'Tajawal','IBM Plex Sans Arabic',-apple-system,Arial,sans-serif",
  mono: "'JetBrains Mono',ui-monospace,Menlo,monospace",
  logo: "https://brand.altibbi.app/logo/lockup@2x.png",
};

const S = {
  h1: `margin:0 0 14px;font-family:${B.display};font-size:27px;line-height:1.25;font-weight:700;color:${B.t1}`,
  h2: `margin:20px 0 10px;font-family:${B.display};font-size:18px;line-height:1.35;font-weight:700;color:${B.t1}`,
  h3: `margin:18px 0 8px;font-family:${B.font};font-size:15px;line-height:1.4;font-weight:600;color:${B.t1}`,
  p: `margin:0 0 18px;font-size:15px;line-height:1.9;color:${B.t2};font-family:${B.font}`,
  ul: "margin:0 0 18px;padding:0 0 0 22px",
  ol: "margin:0 0 18px;padding:0 0 0 22px",
  li: `margin:0 0 6px;font-size:15px;line-height:1.55;color:${B.t2}`,
  a: `color:${B.blue};text-decoration:none;font-weight:600`,
};

export const MAX_EMAIL_LENGTH = 254; // RFC 5321 forward-path limit

export function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length <= MAX_EMAIL_LENGTH && EMAIL_RE.test(trimmed);
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * An image the preview can draw. Sample content uses inline SVG, which no mail
 * client would accept — so this is deliberately looser than publicUrl, and
 * preflight still requires https before anything is sent.
 */
export function previewImageUrl(value: string | undefined): string | null {
  const url = (value ?? "").trim();
  if (url.startsWith("data:image/")) return url;
  return publicUrl(url);
}

export function publicUrl(value: string | undefined): string | null {
  const url = (value ?? "").trim();
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return null;
}

/** The far side: where the masthead sits, opposite the logo. */
function endAlign(dir: "ltr" | "rtl"): "left" | "right" {
  return dir === "rtl" ? "left" : "right";
}

function startAlign(dir: "ltr" | "rtl"): "left" | "right" {
  return dir === "rtl" ? "right" : "left";
}

function inlineMarkdown(value: string): string {
  let s = escapeHtml(value);
  s = s.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_, label: string, href: string) => `<a target="_blank" rel="noopener noreferrer" href="${href}" style="${S.a}">${label}</a>`,
  );
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  s = s.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  s = s.replace(/(^|[\s(])_([^_]+)_/g, `$1<em>$2</em>`);
  return s;
}

/** Turn a text block into email HTML. Plain copy still works; markdown is optional. */
export function markdownToHtml(src: string): string {
  const text = src.replace(/\r\n/g, "\n").trim();
  if (!text) return "";
  const lines = text.split("\n");
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    if (!lines[i].trim()) {
      i += 1;
      continue;
    }

    const heading = /^(#{1,3})\s+(.+)$/.exec(lines[i].trim());
    if (heading) {
      const tag = heading[1].length === 1 ? "h1" : heading[1].length === 2 ? "h2" : "h3";
      const style = tag === "h1" ? S.h1 : tag === "h2" ? S.h2 : S.h3;
      out.push(`<${tag} style="${style}">${inlineMarkdown(heading[2])}</${tag}>`);
      i += 1;
      continue;
    }

    if (/^\s*[-*]\s+/.test(lines[i])) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(`<li style="${S.li}">${inlineMarkdown(lines[i].replace(/^\s*[-*]\s+/, ""))}</li>`);
        i += 1;
      }
      out.push(`<ul style="${S.ul}">${items.join("")}</ul>`);
      continue;
    }

    if (/^\s*\d+\.\s+/.test(lines[i])) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(`<li style="${S.li}">${inlineMarkdown(lines[i].replace(/^\s*\d+\.\s+/, ""))}</li>`);
        i += 1;
      }
      out.push(`<ol style="${S.ol}">${items.join("")}</ol>`);
      continue;
    }

    const para: string[] = [lines[i]];
    i += 1;
    while (
      i < lines.length
      && lines[i].trim()
      && !/^(#{1,3})\s+/.test(lines[i].trim())
      && !/^\s*[-*]\s+/.test(lines[i])
      && !/^\s*\d+\.\s+/.test(lines[i])
    ) {
      para.push(lines[i]);
      i += 1;
    }
    out.push(`<p style="${S.p}">${inlineMarkdown(para.join(" "))}</p>`);
  }
  return out.join("");
}

export function brandCtaButton(label: string, href: string): string {
  return `<table role="presentation" class="altibbi-ctawrap" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 16px">
      <tr>
        <td align="center" class="altibbi-ctacell" bgcolor="${B.blue}" style="border-radius:999px;background-color:${B.blue};">
          <a target="_blank" rel="noopener noreferrer" class="altibbi-cta" href="${escapeHtml(href)}" style="display:inline-block;min-width:128px;padding:14px 28px;border-radius:999px;color:#FFFFFF;font-size:15px;line-height:20px;font-weight:600;font-family:${B.font};text-align:center;text-decoration:none">${escapeHtml(label)}</a>
        </td>
      </tr>
    </table>`;
}

export function brandSecondaryLink(label: string, href: string): string {
  return `<div style="margin-top:12px">
      <a target="_blank" rel="noopener noreferrer" class="altibbi-sec" href="${escapeHtml(href)}" style="display:inline-block;padding:12px 24px;border-radius:999px;background:${B.card};border:1px solid ${B.border};color:${B.t1};font-size:14px;font-weight:600;font-family:${B.font};text-decoration:none">${escapeHtml(label)}</a>
    </div>`;
}

export function brandDetailRows(rows: { label: string; value: string }[]): string {
  if (!rows.length) return "";
  const items = rows.map((row) => `<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>
      <td align="left" style="padding:7px 0;font-size:13px;color:${B.t3};font-family:${B.font}">${escapeHtml(row.label)}</td>
      <td align="right" style="padding:7px 0;font-size:13px;font-weight:600;color:${B.t1};font-family:${B.mono}">${escapeHtml(row.value)}</td>
    </tr></table>`).join("");
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 22px;border:1px solid ${B.border};border-radius:10px"><tr><td style="padding:6px 18px">${items}</td></tr></table>`;
}

function articleCard(title: string, href: string, category?: string): string {
  const cat = category?.trim();
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 16px;border:1px solid ${B.border};border-radius:10px">
    <tr>
      <td style="padding:14px 18px">
        ${cat ? `<div style="font-size:11px;line-height:16px;color:${B.t3};margin:0 0 6px;font-family:${B.font}">${escapeHtml(cat)}</div>` : ""}
        <a target="_blank" rel="noopener noreferrer" href="${escapeHtml(href)}" style="color:${B.t1};font-size:15px;font-weight:600;line-height:1.4;text-decoration:none;font-family:${B.font}">${escapeHtml(title)}</a>
        <div style="margin-top:10px">
          <a target="_blank" rel="noopener noreferrer" href="${escapeHtml(href)}" style="${S.a};font-size:13px">Read on Altibbi</a>
        </div>
      </td>
    </tr>
  </table>`;
}

function trackedHref(url: string | undefined, utm: UtmParams | undefined, content?: string): string | null {
  const href = publicUrl(url);
  if (!href) return null;
  return appendUtm(href, utm, content);
}

export function renderBlocksHtml(blocks: PreviewBlock[], utm?: UtmParams): string {
  return blocks.map((block) => {
    if (block.type === "divider") {
      return `<hr style="border:none;border-top:1px solid ${B.border};margin:16px 0" />`;
    }
    if (block.type === "heading") {
      const text = (block.text || "").trim() || "Heading";
      return `<h2 style="${S.h2}">${inlineMarkdown(text)}</h2>`;
    }
    if (block.type === "text") {
      return markdownToHtml(block.text || "") || `<p style="${S.p}"></p>`;
    }
    if (block.type === "tip") {
      const text = (block.text || "").trim() || "Health tip";
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 18px;border:1px solid ${B.border};border-radius:10px"><tr><td style="padding:14px 18px">
        <div style="font-size:11px;font-weight:700;letter-spacing:.04em;color:${B.blue};margin-bottom:6px;font-family:${B.font}">HEALTH TIP</div>
        <div style="${S.p};margin:0">${inlineMarkdown(text)}</div>
      </td></tr></table>`;
    }
    if (block.type === "footer") {
      const text = (block.text || "").trim() || "You received this because you subscribed to Altibbi newsletters.";
      return `<p style="margin:16px 0 0;font-size:11px;line-height:1.6;color:${B.t3};font-family:${B.font}">${inlineMarkdown(text)}</p>`;
    }
    if (block.type === "image" || block.type === "hero") {
      const src = publicUrl(block.url);
      if (!src && block.type === "hero") {
        const text = (block.text || "").trim();
        return text ? `<h1 style="${S.h1}">${inlineMarkdown(text)}</h1>` : "";
      }
      if (!src) return "";
      const caption = (block.text || "").trim();
      return `<div style="margin:0 0 18px">
        <img src="${escapeHtml(src)}" alt="${escapeHtml(block.alt || "")}" style="width:100%;border-radius:10px;display:block" />
        ${caption ? `<div style="font-size:${block.type === "hero" ? "23px" : "12px"};font-weight:${block.type === "hero" ? "700" : "400"};line-height:1.3;color:${B.t1};margin-top:10px;font-family:${block.type === "hero" ? B.display : B.font}">${escapeHtml(caption)}</div>` : ""}
      </div>`;
    }
    const href = trackedHref(block.url, utm, block.id || block.type);
    if (!href) return "";
    if (block.type === "article") {
      const title = (block.text || "").trim() || "Read on Altibbi";
      const category = (block.text || "").trim() ? (block.alt || "").trim() : "";
      return articleCard(title, href, category || undefined);
    }
    if (block.type === "link") {
      const label = (block.text || "").trim() || "Read more";
      return brandSecondaryLink(label, href);
    }
    const label = (block.text || "").trim() || "Read more";
    return brandCtaButton(label, href);
  }).join("");
}

function renderArticlesHtml(articles: PreviewArticle[], utm?: UtmParams): string {
  return articles
    .filter((a) => publicUrl(a.url))
    .map((a) => articleCard(a.title || "Read on Altibbi", appendUtm(publicUrl(a.url) as string, utm, "library"), a.category))
    .join("");
}

/** The category name above the content — the editorial anchor of the issue. */
function categoryHeadingHtml(label: string): string {
  // An empty label drew a heading with no words and a bare blue rule under
  // it — a stray line in the middle of the email.
  if (!label.trim()) return "";
  return `
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
    <tr><td style="padding:4px 0 14px">
      <div class="altibbi-cat" style="font-family:${B.display};font-size:18px;line-height:1.3;font-weight:700;color:${B.t1}">${escapeHtml(label)}</div>
    </td></tr>
  </table>`;
}

function sectionHeadingHtml(label: string): string {
  return `
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
    <tr><td style="padding:28px 0 12px">
      <div class="altibbi-h2" style="font-family:${B.display};font-size:17px;line-height:1.35;font-weight:700;color:${B.t1}">${escapeHtml(label)}</div>
    </td></tr>
  </table>`;
}

/** The lead story: the same link shape as any other, given more room. */
function featuredHtml(a: PreviewSectionArticle, utm?: UtmParams): string {
  const href = trackedHref(a.url, utm, "featured");
  if (!href && !a.title) return "";
  // A featured article defaults to its picture above; a secondary one to its
  // picture alongside. Either can be changed per link.
  const withDefault = { ...a, layout: a.layout ?? "stacked" as const };
  return `
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"
    class="altibbi-feat" style="background:${B.card};border:1px solid ${B.border};border-radius:12px;overflow:hidden">
    <tr><td class="altibbi-featpad" style="padding:4px 20px 8px">
      ${linkHtml(withDefault, href, { square: 128, titleSize: 20, descSize: 15, cta: "اقرأ المزيد ←", scale: "lg" })}
    </td></tr>
  </table>`;
}

/**
 * One link, its description, and its picture wherever the editor put it.
 *
 * Every link is the same shape — address, words about it, optional picture —
 * so featured and secondary stories share this and differ only in scale.
 * The picture's position is the link's own, not the section's: a list can
 * have one article with the picture above and the next with it alongside.
 */
function linkHtml(
  a: PreviewSectionArticle,
  href: string | null,
  opts: { square: number; titleSize: number; descSize: number; cta: string; scale: "lg" | "sm" },
): string {
  const layout = a.layout ?? "image-start";
  const img = layout === "text" ? null : previewImageUrl(a.image);
  const meta = [a.category, a.read_minutes ? `${a.read_minutes} دقائق قراءة` : ""]
    .filter(Boolean).join(" · ");

  const link = (inner: string, extra = "", cls = "") =>
    href
      ? `<a target="_blank" rel="noopener noreferrer"${cls ? ` class="${cls}"` : ""} href="${escapeHtml(href)}" style="${extra}">${inner}</a>`
      : inner;

  const wide = `<img src="${escapeHtml(img ?? "")}" alt="" width="100%"
     style="width:100%;max-height:220px;object-fit:cover;display:block;border:0;border-radius:8px" />`;
  const square = `<img src="${escapeHtml(img ?? "")}" alt=""
     width="${opts.square}" height="${opts.square}"
     style="width:${opts.square}px;height:${opts.square}px;object-fit:cover;display:block;border:0;border-radius:8px" />`;

  const words = `
    ${meta ? `<div style="font-size:12px;line-height:16px;color:${B.t3};font-family:${B.font};font-weight:500;margin-bottom:6px">${escapeHtml(meta)}</div>` : ""}
    ${link(escapeHtml(a.title), `font-family:${B.font};font-size:${opts.titleSize}px;line-height:1.45;font-weight:600;color:${B.t1};text-decoration:none`, `altibbi-t-${opts.scale}`)}
    ${a.excerpt ? `<div class="altibbi-d-${opts.scale}" style="margin-top:6px;font-size:${opts.descSize}px;line-height:1.9;color:${B.t2};font-family:${B.font}">${escapeHtml(a.excerpt)}</div>` : ""}
    ${href && opts.scale === "lg" ? `<div class="altibbi-more-${opts.scale}" style="margin-top:9px"><a target="_blank" rel="noopener noreferrer" href="${escapeHtml(href)}" style="${S.a};font-size:13px;font-weight:600">${escapeHtml(opts.cta)}</a></div>` : ""}`;

  if (!img) {
    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
      <td style="padding:14px 0">${words}</td></tr></table>`;
  }

  // Above and below stack; before and after keep a square on its side at
  // every width, the same way a content block does.
  if (layout === "stacked" || layout === "image-below") {
    const picture = `<tr><td style="padding:${layout === "stacked" ? "14px 0 0" : "12px 0 14px"}">${link(wide, "display:block")}</td></tr>`;
    const copy = `<tr><td style="padding:${layout === "stacked" ? "12px 0 14px" : "14px 0 0"}">${words}</td></tr>`;
    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
      ${layout === "stacked" ? picture + copy : copy + picture}
    </table>`;
  }

  const pictureTd = `<td class="altibbi-sq-${opts.scale}" width="${opts.square}" valign="top" style="width:${opts.square}px;padding:14px 0">${link(square, "display:block")}</td>`;
  const gapTd = `<td class="altibbi-gap" width="14" style="width:14px">&nbsp;</td>`;
  const copyTd = `<td valign="top" style="padding:14px 0">${words}</td>`;
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
    ${(layout === "image-start" ? [pictureTd, gapTd, copyTd] : [copyTd, gapTd, pictureTd]).join("")}
  </tr></table>`;
}

/** Secondary stories. A list reads better in Arabic; a grid suits short titles. */
function articleListHtml(list: PreviewSectionArticle[], layout: "list" | "grid", utm?: UtmParams): string {
  const rows = list.map((a, i) => {
    if (!a.title) return "";
    const href = trackedHref(a.url, utm, `article-${i + 1}`);
    return `<div style="border-top:1px solid ${B.hair}">${linkHtml(a, href, {
      square: 72, titleSize: 15, descSize: 13, cta: "اقرأ المزيد ←", scale: "sm" as const,
    })}</div>`;
  }).join("");
  // Grid and list share markup: email clients cannot be trusted with real
  // columns at narrow widths, and a stacked list is legible everywhere.
  return `<div data-layout="${layout}">${rows}</div>`;
}

/**
 * A content block: picture, heading, description and link, arranged one of
 * six ways — the picture above, below, before or after the words, no picture
 * at all, or the words over it.
 *
 * Beside the words is a table row, because email clients cannot be trusted
 * with real columns. The picture cell is a fixed square that shrinks on a
 * narrow screen rather than dropping below the words: it collapsed before,
 * which made "before" and "after" render exactly like "above".
 */
/** Side-by-side picture size. Square, so the crop is predictable. */
const SQUARE = 168;
const SQUARE_NARROW = 104;

function blockHtml(section: PreviewSection, utm?: UtmParams): string {
  const layout = (section.layout ?? "stacked") as
    "stacked" | "image-below" | "image-start" | "image-end" | "text" | "overlay";
  const href = trackedHref(section.url, utm, "block");
  const img = previewImageUrl(section.image);
  const heading = section.heading?.trim() ?? "";
  const text = section.text?.trim() ?? "";
  const label = section.cta_label?.trim() || "اعرف المزيد";

  if (!heading && !text && !img) return "";

  if (layout === "overlay") {
    // A real overlay, not the banner's image-only treatment: the picture is
    // the cell's background so the copy sits on top of it. Outlook ignores a
    // CSS background image and shows the brand fill instead, which is why the
    // text must stay legible against that colour on its own.
    const bg = img
      ? `background="${escapeHtml(img)}" style="background:${B.blue} url('${escapeHtml(img)}') center/cover no-repeat;"`
      : `style="background:${B.blue}"`;
    const inner = `
      <div style="padding:26px 22px;text-align:center">
        ${heading ? `<div class="altibbi-h2" style="font-family:${B.display};font-size:19px;font-weight:700;color:#FFFFFF">${escapeHtml(heading)}</div>` : ""}
        ${text ? `<div style="margin-top:7px;font-size:14px;line-height:1.9;color:${B.tint};font-family:${B.font}">${escapeHtml(text)}</div>` : ""}
        ${href ? `<div style="margin-top:15px">${brandSecondaryLink(label, href)}</div>` : ""}
      </div>`;
    return `
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"
      style="margin:24px 0;border-radius:14px;overflow:hidden">
      <tr><td ${bg}>${inner}</td></tr>
    </table>`;
  }

  const picture = img
    ? `<img src="${escapeHtml(img)}" alt="" width="100%"
         style="width:100%;max-width:100%;display:block;border:0;border-radius:12px" />`
    : "";
  const pictureCell = href && picture
    ? `<a target="_blank" rel="noopener noreferrer" href="${escapeHtml(href)}" style="display:block">${picture}</a>`
    : picture;

  const copy = `
    ${heading ? `<div class="altibbi-h2" style="font-family:${B.display};font-size:19px;line-height:1.35;font-weight:700;color:${B.t1}">${escapeHtml(heading)}</div>` : ""}
    ${text ? `<div class="altibbi-copy" style="margin-top:${heading ? "8px" : "0"};font-size:15px;line-height:1.9;color:${B.t2};font-family:${B.font}">${escapeHtml(text)}</div>` : ""}
    ${href ? `<div style="margin-top:14px">${brandSecondaryLink(label, href)}</div>` : ""}`;

  if (layout === "text" || !picture) {
    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:20px 0">
      <tr><td>${copy}</td></tr>
    </table>`;
  }

  if (layout === "stacked" || layout === "image-below") {
    const rows = layout === "stacked"
      ? [`<tr><td>${pictureCell}</td></tr>`, `<tr><td style="padding-top:14px">${copy}</td></tr>`]
      : [`<tr><td>${copy}</td></tr>`, `<tr><td style="padding-top:14px">${pictureCell}</td></tr>`];
    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:20px 0">
      ${rows.join("")}
    </table>`;
  }

  // Beside the words means beside them, at every width. A proportional
  // picture cell had to collapse to one column on a narrow screen, which made
  // "before" and "after" render exactly like "above" — the two arrangements
  // were indistinguishable wherever it mattered. A fixed square shrinks
  // instead of stacking, so the picture stays on its side.
  const square = `<img src="${escapeHtml(img ?? "")}" alt=""
     width="${SQUARE}" height="${SQUARE}"
     style="width:${SQUARE}px;height:${SQUARE}px;object-fit:cover;display:block;border:0;border-radius:12px" />`;
  const squareCell = href
    ? `<a target="_blank" rel="noopener noreferrer" href="${escapeHtml(href)}" style="display:block">${square}</a>`
    : square;

  const pictureTd = `<td class="altibbi-sq" width="${SQUARE}" valign="top" style="width:${SQUARE}px">${squareCell}</td>`;
  const gapTd = `<td class="altibbi-gap" width="16" style="width:16px">&nbsp;</td>`;
  const copyTd = `<td valign="middle">${copy}</td>`;

  const cells = layout === "image-start"
    ? [pictureTd, gapTd, copyTd]
    : [copyTd, gapTd, pictureTd];

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:20px 0">
    <tr>${cells.join("")}</tr>
  </table>`;
}

/** A full-width band that separates one part of the issue from the next. */
function bannerHtml(section: PreviewSection, utm?: UtmParams): string {
  const href = trackedHref(section.url, utm, "banner");
  const img = previewImageUrl(section.image);
  // Nothing to say and nothing to show: draw nothing rather than a coloured
  // band around empty space.
  if (!img && !section.heading?.trim() && !section.text?.trim()) return "";
  const inner = img
    ? `<img src="${escapeHtml(img)}" alt="${escapeHtml(section.heading ?? "")}" width="100%" style="width:100%;display:block;border:0" />`
    : `<div class="altibbi-band" style="padding:26px 22px;text-align:center">
         <div class="altibbi-h2" style="font-family:${B.display};font-size:19px;font-weight:700;color:${B.t1}">${escapeHtml(section.heading || "")}</div>
         ${section.text ? `<div style="margin-top:7px;font-size:14px;line-height:1.6;color:${B.t2};font-family:${B.font}">${escapeHtml(section.text)}</div>` : ""}
         ${href ? `<div style="margin-top:16px">${brandCtaButton(section.cta_label || "اعرف المزيد", href)}</div>` : ""}
       </div>`;
  return `
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"
    style="margin:28px 0;border-radius:12px;overflow:hidden;background:${B.tint};border:1px solid ${B.blueSoft}">
    <tr><td>${href && img ? `<a target="_blank" rel="noopener noreferrer" href="${escapeHtml(href)}" style="display:block">${inner}</a>` : inner}</td></tr>
  </table>`;
}

/** Names for the editor canvas's empty-block placeholders. Never sent. */
const EDITOR_LABEL: Record<string, string> = {
  category: "Category heading", featured: "Featured link", articles: "Link cards",
  block: "Content block", banner: "Banner", tip: "Health tip", doctor: "Doctor note",
  image: "Photo", cta: "Call to action", text: "Text", divider: "Divider", spacer: "Spacer",
};

export function renderSectionsHtml(
  sections: PreviewSection[],
  utm?: UtmParams,
  /** The reader's category, for a section that follows it rather than naming one. */
  categoryHeading?: string,
  /**
   * Editor canvas only: wrap each section in an element carrying its id, so a
   * click on the rendered email can say which block it landed on. Never set on
   * the send path — subscribers get exactly the markup they always did.
   */
  editable = false,
): string {
  if (editable) {
    return sections.map((s) => {
      // The real footer is drawn by the shell after every section; its slot in
      // the list has nothing of its own to click.
      if (s.type === "footer") return "";
      const inner = renderSectionsHtml([s], utm, categoryHeading, false);
      // An empty block sends nothing, so it would also be invisible here and
      // impossible to click. On the canvas only, it gets a placeholder.
      const body = inner || `<div class="altibbi-editor-empty">${escapeHtml(EDITOR_LABEL[s.type] ?? s.type)} — empty, click to fill in</div>`;
      return `<div data-section-id="${escapeHtml(s.id)}" data-section-type="${escapeHtml(s.type)}"${inner ? "" : ' data-empty="true"'}>${body}</div>`;
    }).join("");
  }
  return sections.map((s) => {
    if (s.type === "divider") {
      return `<table role="presentation" width="100%"><tr><td style="padding:18px 0"><div style="height:1px;background:${B.border}"></div></td></tr></table>`;
    }
    if (s.type === "spacer") {
      return `<div style="height:28px;line-height:28px">&nbsp;</div>`;
    }
    if (s.type === "category") {
      return categoryHeadingHtml(s.heading?.trim() || categoryHeading || "");
    }
    if (s.type === "footer") {
      // The real footer is appended by renderBrandEmail; this only reserves
      // the section's place in the structure the editor is arranging.
      return "";
    }
    if (s.type === "doctor") {
      // No recommendation means no section: the label alone arrived as an
      // empty box, while the editor told its author it would not be sent.
      if (!s.text?.trim()) return "";
      const href = trackedHref(s.url, utm, "doctor");
      return `
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"
        style="margin:20px 0;border:1px solid ${B.border};border-radius:12px">
        <tr><td style="padding:16px 18px">
          <div style="font-size:12px;font-weight:700;color:${B.blue};font-family:${B.font};margin-bottom:5px">${escapeHtml(s.heading || "توصية من الطبي")}</div>
          <div class="altibbi-copy" style="font-size:15px;line-height:1.9;color:${B.t1};font-family:${B.font}">${escapeHtml(s.text || "")}</div>
          ${href ? `<div style="margin-top:12px">${brandSecondaryLink(s.cta_label || "اعرف المزيد", href)}</div>` : ""}
        </td></tr>
      </table>`;
    }
    if (s.type === "block") return blockHtml(s, utm);
    if (s.type === "banner") return bannerHtml(s, utm);
    if (s.type === "featured") {
      const a = s.articles?.[0];
      // No article means no section — a heading on its own is furniture.
      if (!a) return "";
      return (s.heading ? sectionHeadingHtml(s.heading) : "") + featuredHtml(a, utm);
    }
    if (s.type === "articles") {
      const list = s.articles ?? [];
      if (list.length === 0) return "";
      return (s.heading ? sectionHeadingHtml(s.heading) : "") + articleListHtml(list, s.layout === "grid" ? "grid" : "list", utm);
    }
    if (s.type === "tip") {
      if (!s.text?.trim()) return "";
      return `
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"
        style="margin:20px 0;background:#E9EFFB;border-radius:12px">
        <tr><td style="padding:16px 18px">
          <div style="font-size:12px;font-weight:700;color:${B.blueDeep};font-family:${B.font};margin-bottom:5px">${escapeHtml(s.heading || "نصيحة صحية")}</div>
          <div class="altibbi-copy" style="font-size:15px;line-height:1.9;color:${B.t1};font-family:${B.font}">${escapeHtml(s.text || "")}</div>
        </td></tr>
      </table>`;
    }
    if (s.type === "image") {
      const img = previewImageUrl(s.image);
      return img ? `<div style="margin:18px 0"><img src="${escapeHtml(img)}" alt="${escapeHtml(s.text || "")}" width="100%" style="width:100%;border-radius:12px;display:block;border:0" /></div>` : "";
    }
    if (s.type === "cta") {
      const href = trackedHref(s.url, utm, "cta");
      return href ? `<div style="margin:22px 0;text-align:center">${brandCtaButton(s.cta_label || "اعرف المزيد", href)}</div>` : "";
    }
    // text
    return s.text ? `<div style="margin:14px 0">${markdownToHtml(s.text)}</div>` : "";
  }).join("");
}

export function renderBodyHtml(issue: PreviewIssue, editable = false): string {
  const sections = issue.sections ?? [];
  const blocks = issue.blocks ?? [];
  const articles = issue.articles ?? [];
  // Sections are the current model; blocks remain for issues authored before
  // the newsletter became category-driven.
  if (sections.length) {
    const heading = issue.category_heading?.trim();
    // A category section already places this heading where the editor put it.
    // Prepending another produced two of them — the second empty, so it
    // arrived as a bare blue rule.
    const hasCategorySection = sections.some((s) => s.type === "category");
    const prefix = heading && !hasCategorySection ? categoryHeadingHtml(heading) : "";
    return prefix + renderSectionsHtml(sections, issue.utm, heading, editable);
  }
  if (blocks.length) return renderBlocksHtml(blocks, issue.utm);
  return renderArticlesHtml(articles, issue.utm);
}

export function renderBrandEmail(opts: BrandEmailOptions): string {
  const dir = opts.dir === "rtl" ? "rtl" : "ltr";
  const align = startAlign(dir);
  const year = new Date().getFullYear();
  const heading = (opts.heading || "").trim();
  const preheader = (opts.preheader || "").trim();
  const footer = opts.footerLines?.length
    ? opts.footerLines
    : [
      "This is an account notification from Altibbi.",
      `<a target="_blank" rel="noopener noreferrer" href="https://altibbi.com/help" style="color:${B.t3};text-decoration:none">Help Center</a> · <a target="_blank" rel="noopener noreferrer" href="https://altibbi.com/privacy" style="color:${B.t3};text-decoration:none">Privacy Policy</a>`,
      `© ${year} Altibbi, Amman, Jordan`,
    ];

  return `<!DOCTYPE html>
<html lang="${dir === "rtl" ? "ar" : "en"}" dir="${dir}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="color-scheme" content="light dark">
<meta name="supported-color-schemes" content="light dark">
<title>${escapeHtml(opts.title)}</title>
<!--[if mso]><style>* { font-family: Arial, sans-serif !important; }</style><![endif]-->
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Arabic:wght@400;500;600;700&family=Tajawal:wght@400;500;700&family=JetBrains+Mono:wght@400;700&display=swap" rel="stylesheet">
<style>
  body { margin:0; padding:0; background:${B.bg}; -webkit-text-size-adjust:100%; }
  a { text-decoration:none; }
  /* ── Phones ───────────────────────────────────────────────────────────
     Desktop values stay inline, so Outlook — which ignores media queries —
     is unaffected by everything below. */
  @media only screen and (max-width:620px){
    .altibbi-email{ width:100%!important; max-width:100%!important; border-radius:14px!important; }
    /* 28px of side padding is a tenth of a phone screen on each edge. */
    .altibbi-outer{ padding:14px 8px!important; }
    .altibbi-pad{ padding-left:18px!important; padding-right:18px!important; }
    .altibbi-head{ padding-top:16px!important; padding-bottom:16px!important; }
    .altibbi-body{ padding-bottom:22px!important; }
    .altibbi-foot{ padding-top:16px!important; padding-bottom:16px!important; }
    .altibbi-h1{ font-size:23px!important; line-height:1.28!important; }
    .altibbi-cat{ font-size:17px!important; }
    /* The body was left at desktop sizes, so a lead story's title arrived at
       20px — the same as the newsletter's own heading — in a 208px column,
       and wrapped to six lines. Everything below the headings comes down
       with them. */
    .altibbi-h2{ font-size:16px!important; }
    .altibbi-t-lg{ font-size:17px!important; }
    .altibbi-t-sm{ font-size:15px!important; }
    .altibbi-d-lg{ font-size:14px!important; line-height:1.9!important; }
    .altibbi-d-sm{ font-size:13px!important; line-height:1.9!important; }
    .altibbi-copy{ font-size:14px!important; line-height:1.9!important; }
    /* A button on a phone is a thumb target: full width, and comfortably
       past the 44px minimum. */
    .altibbi-ctawrap{ width:100%!important; }
    .altibbi-ctacell{ display:block!important; width:100%!important; }
    .altibbi-cta{ display:block!important; width:100%!important; box-sizing:border-box!important; padding:15px 20px!important; }
    .altibbi-sec{ display:block!important; text-align:center!important; padding:13px 20px!important; }
    /* A picture beside the words stays beside them; it is the square that
       gives way, not the arrangement. Stacking here made "before" and
       "after" look exactly like "above". */
    .altibbi-sq{ width:${SQUARE_NARROW}px!important; }
    .altibbi-sq img{ width:${SQUARE_NARROW}px!important; height:${SQUARE_NARROW}px!important; }
    /* The lead story's picture stays larger than a list thumbnail, so the
       hierarchy survives the narrowest screen. */
    .altibbi-sq-lg{ width:132px!important; }
    .altibbi-sq-lg img{ width:132px!important; height:132px!important; }
    .altibbi-sq-sm{ width:96px!important; }
    .altibbi-sq-sm img{ width:96px!important; height:96px!important; }
    .altibbi-gap{ width:12px!important; }
    /* A card inside the body card costs 42px of a 390px screen. On a phone the
       lead story runs the full width and earns its weight from type and picture. */
    .altibbi-feat{ border:0!important; border-radius:0!important; }
    .altibbi-featpad{ padding-left:0!important; padding-right:0!important; }
    .altibbi-band{ padding:22px 18px!important; }
  }
  /* ── Small phones ─────────────────────────────────────────────────────
     At 320–375px a 104px square leaves too little for the words beside it. */
  @media only screen and (max-width:400px){
    .altibbi-pad{ padding-left:14px!important; padding-right:14px!important; }
    .altibbi-h1{ font-size:21px!important; }
    .altibbi-cat{ font-size:16px!important; }
    .altibbi-h2{ font-size:15px!important; }
    .altibbi-t-lg{ font-size:16px!important; }
    .altibbi-t-sm{ font-size:14px!important; }
    .altibbi-d-lg{ font-size:13px!important; }
    .altibbi-d-sm{ font-size:13px!important; }
    .altibbi-copy{ font-size:13px!important; }
    .altibbi-sq{ width:84px!important; }
    .altibbi-sq img{ width:84px!important; height:84px!important; }
    .altibbi-sq-lg{ width:112px!important; }
    .altibbi-sq-lg img{ width:112px!important; height:112px!important; }
    .altibbi-sq-sm{ width:84px!important; }
    .altibbi-sq-sm img{ width:84px!important; height:84px!important; }
    .altibbi-gap{ width:10px!important; }
    .altibbi-band{ padding:20px 14px!important; }
  }
</style>
</head>
<body style="margin:0;padding:0;background:${B.bg};">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:${B.bg};">
    <tr><td align="center" class="altibbi-outer" style="padding:28px 12px;">
      ${preheader ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;line-height:0;">${escapeHtml(preheader)}</div>` : ""}
      <table role="presentation" class="altibbi-email" dir="${dir}" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;background:${B.card};border:1px solid ${B.border};border-radius:16px;border-collapse:separate;overflow:hidden;font-family:${B.font};">
        <tr>
          <td class="altibbi-pad altibbi-head" style="padding:20px 28px;border-bottom:1px solid ${B.border};">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
              <tr>
                <td align="${align}" style="vertical-align:middle;">
                  <img src="${B.logo}" height="26" alt="Altibbi" style="display:block;height:26px;">
                </td>
                ${opts.masthead ? `<td align="${endAlign(dir)}" class="altibbi-mast" style="vertical-align:middle;font-size:12px;line-height:16px;color:${B.t3};font-family:${B.font};white-space:nowrap;">${escapeHtml(opts.masthead)}</td>` : ""}
              </tr>
            </table>
          </td>
        </tr>
        ${opts.iconUrl ? `<tr><td align="${align}" class="altibbi-pad" style="padding:26px 28px 0;"><img src="${escapeHtml(opts.iconUrl)}" width="44" height="44" alt="" style="display:block;"></td></tr>` : ""}
        <tr>
          <td align="${align}" class="altibbi-pad altibbi-body" style="padding:${opts.iconUrl ? "16px" : heading ? "26px" : "22px"} 28px 28px;">
            ${heading ? `<h1 class="altibbi-h1" style="${S.h1}">${heading}</h1>` : ""}
            ${opts.bodyHtml}
          </td>
        </tr>
        <tr>
          <td align="${align}" class="altibbi-pad altibbi-foot" style="padding:18px 28px;border-top:1px solid ${B.border};background:${B.bg};">
            ${footer.map((line) => `<div dir="auto" style="font-size:11px;line-height:1.6;color:${B.t3};margin:0 0 3px;font-family:${B.font}">${line}</div>`).join("")}
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

/** Everything a reader would see, for deciding which way the email runs. */
function issueText(issue: PreviewIssue): string {
  return [
    issue.subject,
    issue.preheader,
    issue.category_heading,
    ...(issue.blocks ?? []).map((b) => b.text ?? ""),
    ...(issue.sections ?? []).flatMap((s) => [
      s.heading ?? "", s.text ?? "",
      ...(s.articles ?? []).flatMap((a) => [a.title, a.excerpt ?? ""]),
    ]),
  ].filter(Boolean).join(" ");
}

/** "نشرة الطبي · 12 أكتوبر 2026" — what this is, and the day it went out. */
const MONTHS_AR = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];
const MONTHS_EN = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function mastheadLine(dir: "ltr" | "rtl", when: Date): string {
  const d = when.getDate();
  const y = when.getFullYear();
  return dir === "rtl"
    ? `نشرة الطبي · ${d} ${MONTHS_AR[when.getMonth()]} ${y}`
    : `Altibbi Newsletter · ${d} ${MONTHS_EN[when.getMonth()]} ${y}`;
}

export function renderPreview(
  issue: PreviewIssue,
  /** `now` is injectable so a test does not depend on the day it runs. */
  options?: {
    dir?: "ltr" | "rtl";
    now?: Date;
    editable?: boolean;
    /**
     * For a real send: the recipient's own unsubscribe link (with a token the
     * send engine fills in), and merge tags left for it to fill per person.
     * Without these the email names a sample reader and links to the site's
     * general unsubscribe page, which is right for a preview and wrong for a
     * subscriber.
     */
    unsubscribeUrl?: string;
    keepMergeTags?: boolean;
  },
): { html: string; text: string } {
  // The internal title is filing — the editor is told "only your team sees
  // this" — so it must not be the email's heading. The subject is the one
  // reader-facing line we have, and it is what they already saw in the inbox.
  const internalName = issue.title.trim();
  const subject = issue.subject?.trim() ?? "";
  const title = subject || internalName || "Untitled newsletter";
  const emailHeading = subject;
  const first = issue.first_name || "سارة";
  const personalize = options?.keepMergeTags
    ? (s: string) => s
    : (s: string) => s.replace(/\{\{\s*first_name\s*\}\}/gi, first).replace(/\{\{\s*name\s*\}\}/gi, first);
  const unsubscribeUrl = options?.unsubscribeUrl ?? "https://altibbi.com/unsubscribe";
  const bodyHtml = personalize(renderBodyHtml(issue, options?.editable === true));
  const empty = !bodyHtml
    ? `<p style="${S.p};color:${B.t3}">This newsletter has no content yet.</p>`
    : "";
  const sender = issue.sender_name || "Altibbi";
  const footerSection = (issue.sections ?? []).find((x) => x.type === "footer");
  const hasFooter = (issue.blocks ?? []).some((b) => b.type === "footer")
    || Boolean(footerSection?.text?.trim());
  const year = new Date().getFullYear();

  // Without an explicit choice, follow the content — an Arabic newsletter
  // sent left-to-right is the preview and the send disagreeing about the
  // thing a reader notices first.
  const dir = options?.dir
    ?? (directionOf(issueText(issue)) === "ltr" ? "ltr" : "rtl");

  // The footer spoke English under Arabic copy — three runs of Latin text at
  // the end of an Arabic newsletter, including the one line a subscriber most
  // needs to understand, which is how to stop receiving it.
  const link = (href: string, label: string) =>
    `<a target="_blank" rel="noopener noreferrer" href="${href}" style="color:${B.t3};text-decoration:none">${label}</a>`;
  const legal = dir === "rtl"
    ? [
      `وصلتك هذه الرسالة لأنك مشترك في نشرات الطبي. ${link(escapeHtml(unsubscribeUrl), "إلغاء الاشتراك")}`,
      `${link("https://altibbi.com/help", "مركز المساعدة")} · ${link("https://altibbi.com/privacy", "سياسة الخصوصية")}`,
      `الطبي، عمّان، الأردن © ${year}`,
    ]
    : [
      `You received this because you subscribed to Altibbi newsletters. ${link(escapeHtml(unsubscribeUrl), "Unsubscribe")}`,
      `${link("https://altibbi.com/help", "Help Center")} · ${link("https://altibbi.com/privacy", "Privacy Policy")}`,
      `© ${year} Altibbi, Amman, Jordan`,
    ];
  // A custom footer adds to the legal lines; it never replaces the one with
  // the unsubscribe link. Bulk mail without a way out breaks the law in most
  // of our markets and Gmail's sender rules.
  const footerLines = [
    hasFooter ? escapeHtml(footerSection?.text?.trim() ?? "") : "",
    legal[0],
    legal[1],
    legal[2],
  ].filter(Boolean);

  const html = renderBrandEmail({
    title: personalize(title),
    masthead: mastheadLine(dir, options?.now ?? new Date()),
    preheader: issue.preheader,
    heading: emailHeading ? escapeHtml(personalize(emailHeading)) : "",
    bodyHtml: `${bodyHtml}${empty}`,
    dir,
    footerLines,
  });

  const blocks = issue.blocks ?? [];
  const articles = issue.articles ?? [];
  const blockText = blocks.length
    ? blocks.map((b) => {
      if (b.type === "heading" || b.type === "text" || b.type === "tip" || b.type === "footer") return b.text || "";
      if (b.type === "link" || b.type === "article" || b.type === "cta") {
        const label = (b.text || "").trim();
        return label ? `${label}\n${b.url || ""}` : (b.url || "");
      }
      if (b.type === "image" || b.type === "hero") return b.text || b.alt || "";
      if (b.type === "divider") return "---";
      return "";
    }).filter(Boolean).join("\n\n")
    : articles.map((a) => `${a.title}\n${a.url}`).join("\n\n");

  const text = [title, issue.preheader || "", "", blockText || "This newsletter has no content yet.", "", `${sender} · https://altibbi.com`, `Unsubscribe: ${unsubscribeUrl}`].join("\n");
  return { html, text };
}

/**
 * Pictures that will not survive the send, because a mail client cannot fetch
 * them. Counted across sections as well as blocks: a section image that is
 * not a public https address would otherwise be dropped without a word.
 */
export function localImageCount(
  blocks: PreviewBlock[] | undefined,
  sections?: PreviewSection[],
): number {
  const fromBlocks = (blocks ?? [])
    .filter((b) => b.type === "image" && b.url && !publicUrl(b.url)).length;
  const fromSections = (sections ?? [])
    .filter((x) => x.image && !publicUrl(x.image)).length;
  return fromBlocks + fromSections;
}
